import { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";

const AudioCtx = createContext(null);

// Map stage index (and extra keys) to audio file
const STAGE_AUDIO = {
  0: "/audio/page1_audio.mp3",
  1: "/audio/page2_audio.mp3",
  2: "/audio/page3_audio.mp3",
  3: "/audio/page4_audio.mp3",
  4: "/audio/page5_audio.mp3",
  5: "/audio/page6_audio.mp3",
  "5b": "/audio/page6_audio2.mp3",   // How Do You Feel after feeling selected
  6: "/audio/page7_audio1.m4a",       // Exhale page
  7: "/audio/page7_audio2.m4a",       // Signup page
  // Block 2 type-specific audio
  "block2_1": "/audio/block2_type1.mp3",
  "block2_2": "/audio/block2_type2.mp3",
  "block2_3": "/audio/block2_type3.mp3",
  "block2_4": "/audio/block2_type4.mp3",
  "block2_5": "/audio/block2_type5.mp3",
};

export function AudioProvider({ children }) {
  const bgMusicRef = useRef(null);
  const stageAudioRef = useRef(null);
  const extraAudioRef = useRef(null);
  const [bgPlaying, setBgPlaying] = useState(false);
  const [bgVolume, setBgVolumeState] = useState(() => {
    const saved = sessionStorage.getItem("pord_bg_volume");
    return saved ? parseFloat(saved) : 0.20;
  });
  const bgVolumeRef = useRef(bgVolume);
  const [stageAudioPlaying, setStageAudioPlaying] = useState(false);
  const [currentStageAudio, setCurrentStageAudio] = useState(null);
  const [extraAudioPlaying, setExtraAudioPlaying] = useState(false);
  const [currentExtraAudio, setCurrentExtraAudio] = useState(null);
  const [bgMusicStarted, setBgMusicStarted] = useState(() => sessionStorage.getItem("pord_music_started") === "true");

  const setBgVolume = useCallback((val) => {
    const clamped = Math.max(0, Math.min(1, val));
    bgVolumeRef.current = clamped;
    setBgVolumeState(clamped);
    sessionStorage.setItem("pord_bg_volume", String(clamped));
    if (bgMusicRef.current) bgMusicRef.current.volume = clamped;
  }, []);

  // Initialize bg music — auto-resume if it was playing before page reload
  useEffect(() => {
    const audio = new Audio("/audio/background_music.mp3");
    audio.loop = true;
    audio.volume = bgVolumeRef.current;
    audio.preload = "auto";
    bgMusicRef.current = audio;

    // Auto-resume if music was started before navigation
    if (sessionStorage.getItem("pord_music_started") === "true") {
      audio.play().then(() => setBgPlaying(true)).catch(() => {});
    }

    return () => { audio.pause(); audio.src = ""; };
  }, []);

  const toggleBgMusic = useCallback(() => {
    if (!bgMusicRef.current) return;
    if (bgPlaying) {
      bgMusicRef.current.pause();
      setBgPlaying(false);
    } else {
      bgMusicRef.current.play().catch(() => {});
      setBgPlaying(true);
      setBgMusicStarted(true);
      sessionStorage.setItem("pord_music_started", "true");
    }
  }, [bgPlaying]);

  const startBgMusic = useCallback(() => {
    if (bgMusicStarted || !bgMusicRef.current) return;
    bgMusicRef.current.play().catch(() => {});
    setBgPlaying(true);
    setBgMusicStarted(true);
    sessionStorage.setItem("pord_music_started", "true");
  }, [bgMusicStarted]);

  const playStageAudio = useCallback((stageIndex) => {
    if (stageAudioRef.current) { stageAudioRef.current.pause(); stageAudioRef.current.src = ""; }
    const audioSrc = STAGE_AUDIO[stageIndex];
    if (!audioSrc) { setStageAudioPlaying(false); setCurrentStageAudio(null); return; }
    const audio = new Audio(audioSrc);
    audio.volume = 0.7;
    stageAudioRef.current = audio;
    setCurrentStageAudio(audioSrc);
    audio.addEventListener("ended", () => setStageAudioPlaying(false));
    audio.play().then(() => setStageAudioPlaying(true)).catch(() => setStageAudioPlaying(false));
  }, []);

  const toggleStageAudio = useCallback((stageIndex) => {
    const audioSrc = STAGE_AUDIO[stageIndex];
    if (!audioSrc) return;
    if (stageAudioPlaying && currentStageAudio === audioSrc) {
      if (stageAudioRef.current) stageAudioRef.current.pause();
      setStageAudioPlaying(false);
    } else {
      playStageAudio(stageIndex);
    }
  }, [stageAudioPlaying, currentStageAudio, playStageAudio]);

  const stopStageAudio = useCallback(() => {
    if (stageAudioRef.current) { stageAudioRef.current.pause(); stageAudioRef.current.src = ""; }
    setStageAudioPlaying(false);
    setCurrentStageAudio(null);
    if (extraAudioRef.current) { extraAudioRef.current.pause(); extraAudioRef.current.src = ""; }
    setExtraAudioPlaying(false);
    setCurrentExtraAudio(null);
  }, []);

  // Play extra audio (e.g. "5b" for How Do You Feel response)
  const playExtraAudio = useCallback((key) => {
    if (extraAudioRef.current) { extraAudioRef.current.pause(); extraAudioRef.current.src = ""; }
    const audioSrc = STAGE_AUDIO[key];
    if (!audioSrc) return;

    if (extraAudioPlaying && currentExtraAudio === audioSrc) {
      setExtraAudioPlaying(false);
      setCurrentExtraAudio(null);
      return;
    }

    const audio = new Audio(audioSrc);
    audio.volume = 0.7;
    extraAudioRef.current = audio;
    setCurrentExtraAudio(audioSrc);
    audio.addEventListener("ended", () => setExtraAudioPlaying(false));
    audio.play().then(() => setExtraAudioPlaying(true)).catch(() => setExtraAudioPlaying(false));
  }, [extraAudioPlaying, currentExtraAudio]);

  const value = {
    bgPlaying, bgVolume, setBgVolume, toggleBgMusic, startBgMusic, bgMusicStarted,
    stageAudioPlaying, currentStageAudio, playStageAudio, toggleStageAudio, stopStageAudio,
    extraAudioPlaying, currentExtraAudio, playExtraAudio,
    STAGE_AUDIO,
  };

  return <AudioCtx.Provider value={value}>{children}</AudioCtx.Provider>;
}

export function useAudio() {
  const ctx = useContext(AudioCtx);
  if (!ctx) throw new Error("useAudio must be used within AudioProvider");
  return ctx;
}
